/**
 * Route overrides for client-side routing decisions.
 * Used to override the default page component based on runtime context
 * (e.g., subdomain, native app, etc.)
 */

import { NEW_WEBSITE_PATH } from './paths';

export const isOrderingContext = () => {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return (
    hostname.startsWith('order.') ||
    hostname.includes('cloudfront') ||
    window.isNativeApp
  );
};

/**
 * New website path patterns (when websiteEnabled is true)
 */
const NEW_WEBSITE_PATTERNS_ENABLED = [
  /^\/$/,                    // home page
  /^\/locations(\/|$)/,      // locations pages
  /^\/areas(\/|$)/,          // area pages
  /^\/menu\/item(\/|$)/,     // menu item detail pages
];

/**
 * New website path patterns (when websiteEnabled is false)
 */
const NEW_WEBSITE_PATTERNS_DISABLED = [
  new RegExp(`^\\/${NEW_WEBSITE_PATH}(\\/|$)`),  // /new-website/*
];

/**
 * Check if a pathname is a new website page
 * Takes into account route overrides (e.g., home page on order subdomain)
 * @param {string} pathname - Current route pathname
 * @param {boolean} websiteEnabled - Whether new website is enabled
 * @returns {boolean}
 */
export const isNewWebsitePage = (pathname, websiteEnabled = false) => {
  // If on ordering context and home page, it's NOT a new website page (overridden)
  if (pathname === '/' && isOrderingContext()) {
    return false;
  }

  const patterns = websiteEnabled
    ? NEW_WEBSITE_PATTERNS_ENABLED
    : NEW_WEBSITE_PATTERNS_DISABLED;

  return patterns.some(pattern => pattern.test(pathname));
};

const routeOverrides = [
  {
    // When on order subdomain/native app, show ordering home instead of new website
    match: (pathname, ctx) =>
      pathname === '/' &&
      ctx.websiteEnabled &&
      ctx.isNewWebsiteLayout,
    condition: isOrderingContext,
    override: {
      component: 'OrderingHome',
      contextOverrides: { isNewWebsiteLayout: false },
    },
  },
];

/**
 * Find a route override for the given pathname and page context
 * @param {string} pathname - Current route pathname
 * @param {object} pageContext - Gatsby page context
 * @returns {object|null} Override config or null if no override applies
 */
export const getRouteOverride = (pathname, pageContext) =>
  routeOverrides.find(
    (r) => r.match(pathname, pageContext) && r.condition()
  )?.override || null;